import type { MockEnvironment } from '@gotti/test-utils';
// `@gotti/sandbox-types` is not a package in this workspace and never has been
// (TS2307). GboxClientSettings lives in @gotti/sandbox-shared — which is where
// SandboxRunnerProvider.tsx and export/gboxFormat.ts already import it from.
import type { GboxClientSettings } from '@gotti/sandbox-shared';
import type {
  TestRunResult as EngineTestRunResult,
  SuiteResult as EngineSuiteResult,
  TestResult as EngineTestResult,
  CoverageReport,
  ProjectTree,
  TestAssetRef,
} from '@gotti/sandbox-core';
// Re-export SystemFile from editor types to maintain single source of truth
export type { SystemFile } from '../editor/types';
import type { SystemFile } from '../editor/types';

export type { TestProgressEvent, TestStatus } from '@gotti/sandbox-core';

/**
 * ── Wall-clock timing: what the engine does and does NOT give us ────────────
 *
 * The test engine (`packages/sandbox-test/src/engine/testRuntime.ts`) removed
 * durations from `TestResult` / `SuiteResult` / `TestRunResult` on purpose: the
 * DualRunner coordinator byte-compares the result JSON for node/browser parity,
 * and a wall-clock number can never match. Durations moved to a separate
 * `RunMetadata` (`durationMs` + `perTestDurations`) that the coordinator does
 * not compare, reachable via `runCollectedTestsWithMetadata()`.
 *
 * This app's runners nonetheless MEASURE the run total themselves
 * (`performance.now() - start` in `SandboxRunnerProvider.runTestsFromFiles` and
 * `SandboxRunner.runTests`), so `duration` on the RUN is real data the engine
 * type simply does not model — it is declared required below.
 *
 * Per-suite / per-test `duration` is a different story and is declared OPTIONAL
 * on purpose: the only rows that ever carry it are the synthetic `load error`
 * rows these runners build by hand (`duration: 0`). Everything produced by
 * `runCollectedTests()` has no duration at all, because this app calls the
 * metadata-less overload. The panels that render it therefore show nothing for
 * real tests today — a live gap, recorded here rather than papered over by
 * declaring a field the producer never sets.
 */
export interface TestResult extends EngineTestResult {
  /** Absent for every engine-produced test — see the note above. A playground test's is its wall-clock run. */
  duration?: number;
  /** A playground test's step tree: which playground and test it is, and every step as the run left it. */
  playground?: { playground: string; id: string; steps: PlaygroundStepView[] };
}

/** A playground step as the Tests panel shows it. */
export interface PlaygroundStepView {
  label: string;
  atTime: number;
  state: 'pending' | 'running' | 'passed' | 'failed';
  durationMs?: number;
  /** `<side> <phase> › <assertion>: <message>` for each error the step raised. */
  errors: string[];
  /** The console lines the step produced, oldest first. */
  logs: string[];
  /** Every assertion of the step: which side and phase ran it, and how it went. */
  checks: PlaygroundCheckView[];
}

/** One assertion of a playground step. */
export interface PlaygroundCheckView {
  side: 'client' | 'server';
  phase: 'beforeUpdate' | 'onUpdate' | 'afterUpdate';
  label: string;
  state: 'awaiting' | 'pending' | 'running' | 'passed' | 'failed';
  errorMessage?: string;
  durationMs?: number;
}

export interface SuiteResult extends Omit<EngineSuiteResult, 'tests'> {
  /** Absent for every engine-produced suite — see the note above. */
  duration?: number;
  tests: TestResult[];
}

export interface TestRunResult extends Omit<EngineTestRunResult, 'suites'> {
  /** Measured by this app around the whole run, not by the engine. */
  duration: number;
  suites: SuiteResult[];
  /** The test a rerun ran: the result is the last results with that one test's replaced. */
  rerun?: { suiteName: string; testName: string };
}

/**
 * A flexible folder structure for runtime input
 */
export interface SandboxFolderInput {
  /** Files at the root of this folder */
  rootFiles: SystemFile[];
  /** Subfolders containing files */
  subfolders: Record<string, SystemFile[]>;
}

export interface SandboxSystemsInput {
  client: SandboxFolderInput;
  server: SandboxFolderInput;
  shared: SandboxFolderInput;
}

/**
 * Default client settings - used when creating new clients
 */
export const DEFAULT_CLIENT_SETTINGS: Required<GboxClientSettings> = {
  simulatedPing: 50,
  simulatedPeerPing: null,
  packetLoss: 0,
  jitter: 0,
};

export interface ClientRecord {
  id: string;
  displayName?: string;
  settings: GboxClientSettings;
  instance?: any; // MockClient from test-utils
}

/**
 * Client record with DOM elements attached (used internally by provider)
 */
export interface ClientRecordWithDOM extends ClientRecord {
  canvas: HTMLCanvasElement;
  container: HTMLElement;
}

export type RunnerStatus = 
  | 'idle'
  | 'compiling'
  | 'initializing'
  | 'creating-client'
  | 'running'
  | 'stopped'
  | 'error';

// ─── Streaming test progress ────────────────────────────────────────────────

export type TestItemState = 'pending' | 'running' | 'passed' | 'failed' | 'error' | 'skipped';

export interface TestProgressItem {
  name: string;
  state: TestItemState;
  duration?: number;
  error?: string;
  /** A playground test's steps as they run. */
  steps?: PlaygroundStepView[];
}

export interface SuiteProgressItem {
  name: string;
  state: TestItemState;
  tests: TestProgressItem[];
  collapsed: boolean;
}

/** Live snapshot of all suites/tests as they execute. */
export interface TestProgressState {
  suites: SuiteProgressItem[];
  phase: 'idle' | 'running' | 'complete';
}

// ─── Context types ──────────────────────────────────────────────────────────

export interface SandboxRunnerState {
  status: RunnerStatus;
  statusText: string;
  isLoading: boolean;
  clients: ClientRecordWithDOM[];
  focusedClientId: string | null;
  environment: MockEnvironment | null;
  testResults: TestRunResult | null;
  testProgress: TestProgressState;
  isRunningTests: boolean;
  coverageReport: CoverageReport | null;
  /**
   * Monotonic signature that changes whenever the agent-file mirror is updated
   * (see `syncAgentFiles`). The Tests panel keys its pre-run discovery effect on
   * this so it re-collects when the AI writes/changes files that the structured
   * editor `systems` cannot represent (e.g. the worker's flat `__tests__/*.spec.ts`).
   */
  agentFilesSig: string;
}

/**
 * A suite (and its test names) discovered for the PRE-RUN preview by loading
 * the test files and reading what they register — accurate even for tests
 * produced by helpers/factories (e.g. `defineSandboxTest`), which regex
 * scanning of the source cannot extract. `file` is the sandbox-relative spec
 * path (e.g. `shared/__tests__/PlayerMovement.spec.ts`).
 */
export interface CollectedTestSuite {
  name: string;
  /** A playground test also carries its id — what a rerun of it names. */
  tests: { name: string; id?: string }[];
  file: string;
  /** A spec file's suite, or one of the project's playgrounds. Absent on a v1 discovery (`collectTests`): a spec suite. */
  kind?: 'spec' | 'playground';
  /** A playground's identity: what mounting it again takes. */
  playground?: { name: string; member?: string; variant?: 'online' | 'offline' };
}

/** The one test a rerun runs: a spec file's test by its suite and name, or a playground's by its id. */
export type RerunTarget =
  | { kind: 'spec'; file: string; suiteName: string; testName: string }
  | { kind: 'playground'; file: string; playground: string; member?: string; variant?: 'online' | 'offline'; testId: string; testLabel: string };

export interface SandboxRunnerActions {
  /** Boots the live preview: the editor's project tree, or a v1 project (the e2e specs and the parity harness pass one), migrated first. */
  run: (input: SandboxSystemsInput | ProjectTree) => Promise<void>;
  stop: () => void;
  addClient: () => Promise<void>;
  /** Tops the running preview up to `count` clients (a restored document's): the project's definition may already boot some or all of them. */
  restoreClients: (count: number) => Promise<void>;
  removeClient: (id: string) => Promise<void>;
  focusClient: (id: string) => void;
  resize: () => void;
  updateClientSettings: (id: string, settings: Partial<GboxClientSettings>) => void;
  getClientSettings: () => { id: string; displayName?: string; settings: GboxClientSettings }[];
  runTests: (systems: SandboxSystemsInput) => Promise<TestRunResult>;
  runTestsFromFiles: (
    files: Record<string, string>,
    testFilePaths?: string[],
    /** Generated assets to preload (overlaid onto the session asset store; refs WIN by path). */
    assets?: TestAssetRef[],
  ) => Promise<TestRunResult>;
  /**
   * Load the test files (without running them) and return the suites/tests
   * they register — for the Tests panel's pre-run preview. Discovery runs
   * against the editor `systems` UNIONED with the agent-file mirror (worker
   * files win by path), so specs the AI holds at paths the editor can't
   * represent are still discovered and shown.
   */
  collectTests: (systems: SandboxSystemsInput) => Promise<CollectedTestSuite[]>;
  /**
   * The project's tests, for the Tests panel's pre-run listing: the spec suites its AI view registers (the agent-file
   * mirror overlaid, synthetic specs left out), then every playground its config discovers that declares tests.
   */
  collectProjectTests: (project: ProjectTree) => Promise<CollectedTestSuite[]>;
  /**
   * The panel's Run All: the project's spec files and every playground test, in one preview transition, as one result
   * — what the panels show and the `test-results` payload carries.
   */
  runAllTests: (project: ProjectTree) => Promise<TestRunResult>;
  /** Runs one test again; the result is the last results with that test's replaced, and says which (`rerun`). */
  rerunTest: (project: ProjectTree, target: RerunTarget) => Promise<TestRunResult>;
  /** Cancels a run the panel started (Run All or a rerun). An AI-requested run is never cancelled. */
  cancelTests: () => void;
  /**
   * Mirror the AI worker's current file set (path → content, any path scheme)
   * into the runner so the Tests panel reflects exactly what the AI holds — the
   * editor's structured `systems` cannot represent the worker's flat
   * `__tests__/*.spec.ts`, so without this the panel shows "No tests found"
   * while the agent is running failing specs. Called on every file the worker
   * emits (file:modified) and on every test run (test:request). Idempotent;
   * only bumps `agentFilesSig` when content actually changes.
   */
  syncAgentFiles: (files: Record<string, string>) => void;
  /**
   * AUTHORITATIVE full-snapshot variant of {@link syncAgentFiles}: REPLACES the
   * mirror with exactly the given set, dropping entries no longer present. Use
   * for the worker's `files:sync` snapshot so deletes/rollbacks/renames
   * propagate (upsert-only `syncAgentFiles` can leave a stale spec the AI
   * dropped). Only bumps `agentFilesSig` when the set actually changes.
   */
  replaceAgentFiles: (files: Record<string, string>) => void;
}

export interface SandboxRunnerContextValue extends SandboxRunnerState, SandboxRunnerActions {}
